import React, { useState } from 'react';
import {
  Button,
  Container,
  FormControl,
  InputLabel,
  Box,
  Modal,
  MenuItem,
  Select,
  TextField,
  Typography,
  Tooltip,
  useTheme,
  IconButton,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const LeaveApply = () => {
  const theme = useTheme();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showUserGuideModal,setShowUserGuideModal]=useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [leaveType, setLeaveType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [numberOfLeaves, setNumberOfLeaves] = useState('');
  const [leaveBalance, setLeaveBalance] = useState('30');
  const [reason, setReason] = useState('');
  const navigate = useNavigate();

  // Static data for dropdowns
  const staticEmployeeData = [
    { employeeId: 1, employeeName: 'John Doe' },
    { employeeId: 2, employeeName: 'Jane Smith' },
  ];

  const staticLeaveTypeData = [
    { leaveTypeId: 1, leaveTypeName: 'Annual Leave' },
    { leaveTypeId: 2, leaveTypeName: 'Sick Leave' },
    { leaveTypeId: 3, leaveTypeName: 'Casual Leave' },
  ];

  const handleSave = (e) => {
    e.preventDefault();
    if (employeeId && leaveType && fromDate && toDate && numberOfLeaves && reason) {
     setShowSuccessModal(true)


    } else {
      setSnackbarMessage('Please fill all fields');
      setOpenSnackbar(true);
      setSnackbarSeverity('error')
    }
  };

  const handleCancel = () => {
    setEmployeeId('');
    setLeaveType('');
    setFromDate('');
    setToDate('');
    setNumberOfLeaves('');
    setReason('');
  };

  const isFormFilled = employeeId && leaveType && fromDate && toDate && numberOfLeaves && reason;

  return (
    <Container sx={{ maxWidth: '100%', boxSizing: 'border-box' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 2,
        }}
      >
        <IconButton
          onClick={() => setShowUserGuideModal(true)}
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            minWidth: 0, // Remove MUI's default padding
          }}
        >
          <InfoOutlineIcon sx={{ fontSize: 30 }} />
        </IconButton>
      </Box>

      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <FormControl fullWidth sx={{ my: 2 }}>
          <InputLabel id="employee-label"
            sx={{ color: theme.palette.text.primary }}
          >Employee</InputLabel>
          <Select
            labelId="employee-label"
            id="employee"

            value={employeeId}
            label="Employee"
            onChange={(e) => setEmployeeId(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <MenuItem value="">
              <em>Select an employee</em>
            </MenuItem>
            {staticEmployeeData.map((employee) => (
              <MenuItem key={employee.employeeId} value={employee.employeeId}>
                {employee.employeeName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ my: 2 }}>
          <InputLabel id="leave-type-label"
            sx={{ color: theme.palette.text.primary }}
          >Leave Type</InputLabel>
          <Select
            labelId="leave-type-label"
            id="leave-type"
            value={leaveType}
            label="Leave Type"
            onChange={(e) => setLeaveType(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <MenuItem value="">
              <em>Select a leave type</em>
            </MenuItem>
            {staticLeaveTypeData.map((leaveTypeItem) => (
              <MenuItem key={leaveTypeItem.leaveTypeId} value={leaveTypeItem.leaveTypeId}>
                {leaveTypeItem.leaveTypeName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
          <TextField
            label="From Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
              },
            }}
          />
          <TextField
            label="To Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
          <TextField
            label="No. of Leaves"
            variant="outlined"
            fullWidth
            value={numberOfLeaves}
            onChange={(e) => setNumberOfLeaves(e.target.value)}
            sx={{

              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
            }}
          />
          <TextField
            label="Leave Balance"
            variant="outlined"
            fullWidth
            value={leaveBalance}
            disabled
            sx={{

              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
            }}
          />
        </Box>

        <TextField
          label="Reason"
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.text.primary,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '& .MuiInputLabel-root': {
              color: theme.palette.text.primary,
            },
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Tooltip
            title={
              !employeeId ? "Select Employee" :
                !leaveType ? "Select Leave Type" :
                  !fromDate ? "Select From Date" :
                    !toDate ? "Select To Date" :
                      !numberOfLeaves ? "Enter Number of Leaves" :
                        !reason ? "Enter Reason" : ""
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  mr: 2,
                  width: '100px',
                  height: '50px',
                  borderRadius: '10px',
                  padding: '10px 0',
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark }

                }}
                disabled={!isFormFilled}
              >
                SAVE
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            sx={{
              width: '100px',
              height: '50px',
              borderRadius: '10px',
              padding: '10px 0',
              fontWeight: 'bold',
            }}
          >
            RESET
          </Button>
        </Box>
      </Paper>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Leave Applied 
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              Leave Applied Successfully
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  setEmployeeId('');
                  setLeaveType('');
                  setFromDate('');
                  setToDate('');
                  setNumberOfLeaves('');
                  setReason('');
                }}
                variant="contained"
                color="primary"
                sx={{
                  borderRadius: '10px',
                  height: '40px',
                  padding: '0 20px',
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark }

                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Snackbar for errors */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* User Guide Modal */}
      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 800,
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Leave Application - User Guide
            </Typography>
            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: '#fff', position: 'absolute', right: 14 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Scrollable Content */}
          <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
  {/* Section: Apply Leave */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
  >
    ➤ Follow these steps to apply for leave:
  </Typography>
  <ul
    style={{
      marginLeft: '20px',
      lineHeight: '1.8',
      cursor: 'pointer',
    }}
  >
    <li>
      Select an <strong>Employee</strong> from the dropdown (required).
    </li>
    <li>
      Select a <strong>Leave Type</strong> (for example, Annual Leave or Sick Leave) from the dropdown (required).
    </li>
    <li>
      Choose the <strong>From Date</strong> and <strong>To Date</strong> using the date pickers (required).
    </li>
    <li>
      Enter the <strong>No. of Leaves</strong> (required). The system calculates this based on the selected dates, but you can adjust it if needed.
    </li>
    <li>
      Review the <strong>Leave Balance</strong> (display-only and automatically fetched in the live application).
    </li>
    <li>
      Enter a <strong>Reason</strong> for the leave in the multiline text field (required).
    </li>
    <li>
      Click <strong>"SAVE"</strong> once all required fields are filled. The button is enabled only when validation passes, and a success modal confirms the application.
    </li>
    <li>
      To clear all fields, click the <strong>"RESET"</strong> button.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: All required fields must be completed. Otherwise, the SAVE button remains disabled and a tooltip indicates the missing field.
  </Typography>

  {/* Section: Success Handling */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ After successful submission:
  </Typography>
  <ul
    style={{
      marginLeft: '20px',
      lineHeight: '1.8',
      cursor: 'pointer',
    }}
  >
    <li>
      A modal message confirms <strong>"Leave Applied Successfully"</strong>.
    </li>
    <li>
      Click <strong>"OK"</strong> to close the modal and reset the form.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: In a live application, the data would be sent to the backend and email notifications would be triggered.
  </Typography>

  {/* Section: Error Handling */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ If fields are missing:
  </Typography>
  <ul
    style={{
      marginLeft: '20px',
      lineHeight: '1.8',
      cursor: 'pointer',
    }}
  >
    <li>
      A snackbar error message displays <strong>"Please fill all fields"</strong>.
    </li>
    <li>
      A tooltip on the <strong>SAVE</strong> button highlights the first missing required field.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Ensure the selected dates are valid and that the To Date is later than the From Date (additional validation can be implemented in the live application).
  </Typography>

  {/* Bottom Close */}
  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
    {/* Optional Close button if required */}
  </Box>
</CustomTableScrollbar>

        </Box>
      </Modal>
    </Container>
  );
};

export default LeaveApply;